/**
 * Edit what a live dataset's next run pulls, with the same filters (Langfuse's or qFlow's) and
 * "Keep per run" as the create dialog. The trace connection stays as it is.
 */

import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { errorText } from "@/components/ui/states";
import { SourceFilters } from "./source-filters";
import { fromFilters, toFilters, type TraceQuery } from "@/features/traces/trace-query";
import { useUiStore } from "@/stores/ui-store";
import { useUpdateSource } from "../hooks";
import type { DatasetSource } from "../types";

const MAX_PULL = 5000;

export function SourceEditForm({
  datasetId,
  source,
  onDone,
}: {
  datasetId: string;
  source: DatasetSource;
  onDone: () => void;
}) {
  const update = useUpdateSource(datasetId);
  const notify = useUiStore((s) => s.notify);
  const [query, setQuery] = useState<TraceQuery>(() => fromFilters(source.filters));
  const [limit, setLimit] = useState(source.limit);
  const [blocked, setBlocked] = useState<string | null>(null);

  const save = (ev: FormEvent) => {
    ev.preventDefault();
    if (blocked) return;
    update.mutate(
      { limit, filters: toFilters(query) },
      {
        onSuccess: onDone,
        onError: (e) => notify(errorText(e, "Could not update the source."), "error"),
      },
    );
  };

  return (
    <form onSubmit={save} className="flex flex-col gap-3.5 px-4 py-3" aria-label="Edit source">
      <SourceFilters
        sourceId={source.connection_id}
        value={query}
        onChange={setQuery}
        onBlocked={setBlocked}
      />
      <Field
        label="Keep per run"
        htmlFor="source-limit"
        hint="At most this many: the first ones from the period start."
      >
        <input
          id="source-limit"
          type="number"
          min={1}
          max={MAX_PULL}
          className="inp w-32 font-mono"
          value={limit}
          onChange={(e) => setLimit(Math.max(1, Math.min(MAX_PULL, Number(e.target.value) || 1)))}
        />
      </Field>
      <p className="text-xs text-muted">Applies to the next run. Versions already pulled do not change.</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" variant="primary" disabled={!!blocked || update.isPending}>
          Save
        </Button>
        <Button onClick={onDone}>Cancel</Button>
        {blocked && <span className="text-xs text-muted">{blocked}</span>}
      </div>
    </form>
  );
}
